/**
 * @param {string|number} key The key to combine on
 * @param {Object} state The existing state to merge against (must contain specified key)
 * @param {Object} newState The new state to shallow merge into existing state
 * @returns {Object} An object containing the key, whose value is the merged state
 */
export const combineOnKey = (key, state, newState) => ({[key]: {...state[key], ...newState}})

/**
 * Merges the new state and old state in an immutable way to provide a new
 * copy of the state (if applicable) or the existing state.
 *
 * @param {Object} state The existing state
 * @param {Object|null} newState Any new state to be merged
 * @returns {Object} The state object
 */
const combine = (state, newState) => {
  if (!newState) {
    return state
  }
  return {
    ...state,
    ...newState,
  }
}

/**
 * @typedef switcherFn
 * @type {function}
 * @param {Object|null} state Any new state to merge from running the reducer
 * @param {Object} action The action that was reduced
 * @param {string} action.type The unique action identifier
 * @returns {Object|null} The new state to be combined or null if no changes
 */

/**
 * A helper function which returns a Redux-valid reducer function signature. The
 * given `switcher` need only return changed state objects, as this helper takes
 * care of (shallow) merging any new changed state with the existing state. If you
 * require a deeper merging of object properties, you must do that in your reducer
 * file (aka, the "switcher").
 *
 * @param {Object|undefined} [initialState] The initial state of your reducer
 * @param {switcherFn} switcher Your reducer function logic
 * @returns {function}
 */
export const reducer = (initialState = {}, switcher) => {
  return (state = initialState, action) => {
    return combine(state, switcher(state, action))
  }
}
